
/**
 * Cart Manager - Handles inventory validation and quantity synchronization
 * Uses Shopify sections API instead of cart.js for better performance
 */


class CartManager {
  constructor() {
    this.conflictItems = [];
    this.cartData = null;
    this.isCheckoutInProgress = false;
    this.currentCheckoutButton = null;
    this.originalButtonText = null;
  }

  /**
   * Main function to validate and sync cart
   * Returns: 'proceed' | 'conflicts' | 'updated' | 'quantity-cancelled'
   */
  async validateAndSyncCart() {
    try {
      return await this._performValidation();
    } catch (error) {
      return 'proceed'; // On error, allow checkout to proceed
    }
  }
  
  async _performValidation() {
    // Fetch cart data using sections API
    const cartData = await this.fetchCartData();
  if (!cartData || cartData.length === 0) return 'proceed';
  this.cartData = cartData;
  const inventoryConflicts = [];

  this.cartData.forEach(item => {
    // Shopify oversell allowed → skip checks
    if (item.inventory_policy === 'continue') {
      console.log("Skipping (Shopify continue):", item.product_title);
      return;
    }

    if (item.quantity > item.inventory_quantity) {
      inventoryConflicts.push({
        ...item,
        availableQty: item.inventory_quantity
      });
    }
  });
    
    if (inventoryConflicts.length > 0) {
      // Show popup for inventory conflicts
      this.conflictItems = inventoryConflicts;
      await this.showInventoryConflictPopup();
      return 'conflicts';
    }
    
    // STEP 2: No inventory conflicts - check and fix yards matching in background
    const yardsFixed = await this.validateAndFixYardsMatching();
    
    if (yardsFixed) {
      // Refresh cart data after yards updates
      this.cartData = await this.fetchCartData();
    }
    
    // No conflicts - proceed to checkout
    return 'proceed';
  }

  /**
   * Fetch cart data using sections API
   */
    async  fetchCartData() {
      try {
        const timestamp = new Date().getTime();
        const response = await fetch(`/?sections=cart-items&t=${timestamp}`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Fetched data:", data);

        if (!data['cart-items']) {
          return [];
        }

        const htmlContent = data['cart-items'];
        const scriptMatch = htmlContent.match(
          /<script[^>]*type=["']application\/json["'][^>]*>([\s\S]*?)<\/script>/i
        );

        if (!scriptMatch) {
          console.warn("No <script> tag with application/json found.");
          return [];
        }

        const jsonString = scriptMatch[1].trim();
        const cartData = JSON.parse(jsonString);

        // Decode HTML entities in the properties
        cartData.forEach(item => {
          if (item.properties && Array.isArray(item.properties)) {
            item.properties = item.properties.map(([key, value]) => [
              this.decodeHTMLEntities(key),
              this.decodeHTMLEntities(value)
            ]);
          }
        });

        console.log("Decoded cart data:", cartData);
        return cartData;
      } catch (error) {
        console.error("Error fetching cart data:", error);
        return [];
      }
    }


  /**
   * Check for inventory conflicts
   */
  decodeHTMLEntities(text) {
  const doc = new DOMParser().parseFromString(text, "text/html");
  return doc.documentElement.textContent;
}

  async checkInventoryConflicts() {
    // Always fetch fresh cart data for conflict checking
    this.cartData = await this.fetchCartData();
    
    if (!this.cartData || this.cartData.length === 0) {
      this.conflictItems = [];
      return this.conflictItems;
    }
    
    this.conflictItems = [];
    
    this.cartData.forEach(item => {
      if (item.inventory_quantity < item.quantity) {
        this.conflictItems.push({
          ...item,
          availableQty: item.inventory_quantity
        });
      }
    });
    
    return this.conflictItems;
  }

  /**
   * Show inventory conflict popup
   */
  showInventoryConflictPopup(conflictItems = null, checkoutButton = null) {
    return new Promise((resolve) => {
      // Use provided conflictItems or use the instance property
      const itemsToShow = conflictItems || this.conflictItems;
      
      if (itemsToShow.length === 0) {
        resolve();
        return;
      }
      
      // Create popup HTML with Quilted Joy theme styling
      const popupHTML = `
        <div id="inventory-conflict-popup" style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(18, 18, 18, 0.6);
          z-index: 999999;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Poppins', Arial, sans-serif;
        ">
          <div style="
            background: #ffffff;
            padding: 40px;
            border-radius: 0;
            max-width: 600px;
            width: 90%;
            max-height: 80vh;
            overflow-y: auto;
            box-shadow: 0 4px 20px rgba(18, 18, 18, 0.15);
            border: 1px solid #f3f3f3;
          ">
            <div style="text-align: center; margin-bottom: 30px;">
              <h2 style="
                margin: 0 0 15px 0;
                color: #0868b1;
                font-size: 28px;
                font-weight: 600;
                font-family: 'Poppins', Arial, sans-serif;
              ">🛒 Cart Update Required</h2>
              <p style="
                margin: 0;
                color: #121212;
                font-size: 16px;
                line-height: 1.5;
              ">Some items in your cart have limited stock available. Please choose how to proceed with each item.</p>
            </div>
            
            <div id="conflict-items-list" style="margin-bottom: 30px;">
              ${itemsToShow.map(item => `
                <div style="
                  border: 2px solid #f3f3f3;
                  padding: 20px;
                  margin: 15px 0;
                  border-radius: 0;
                  background: #ffffff;
                  transition: all 0.3s ease;
                " data-key="${item.key}" onmouseover="this.style.borderColor='#0868b1'" onmouseout="this.style.borderColor='#f3f3f3'">
                  ${item.image ? `<div style="display: flex; align-items: center; margin-bottom: 15px;">
                    <img src="${item.image}" alt="${this.getItemTitle(item)}" style="
                      width: 60px;
                      height: 60px;
                      object-fit: cover;
                      margin-right: 15px;
                      border: 1px solid #f3f3f3;
                    ">
                    <div>
                      <h4 style="margin: 0 0 5px 0; color: #121212; font-size: 16px; font-weight: 500;">${this.getItemTitle(item)}</h4>
                      ${item.sku ? `<p style="margin: 0; color: #666; font-size: 14px;">SKU: ${item.sku}</p>` : ''}
                    </div>
                  </div>` : `<h4 style="margin: 0 0 15px 0; color: #121212; font-size: 16px; font-weight: 500;">${this.getItemTitle(item)}</h4>`}
                  
                  <div style="
                     background: #f3f3f3;
                     padding: 15px;
                     border-radius: 0;
                     margin-bottom: 15px;
                   ">
                     <div style="display: flex; justify-content: space-between; align-items: center;">
                       <span style="color: #121212; font-weight: 500;">Requested Quantity:</span>
                       <span style="color: #0868b1; font-weight: 600; font-size: 18px;">${this.hasDesiredQuantityProperty(item) ? (item.quantity * 0.25) + ' Yards' : item.quantity}</span>
                     </div>
                     <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px;">
                       <span style="color: #121212; font-weight: 500;">Available Stock:</span>
                       <span style="color: ${item.availableQty > 0 ? '#0868b1' : '#d32f2f'}; font-weight: 600; font-size: 18px;">${this.hasDesiredQuantityProperty(item) ? (item.availableQty * 0.25) + ' Yards' : item.availableQty}</span>
                     </div>
                   </div>
                  
                  <div style="display: flex; gap: 10px; flex-wrap: wrap;">
                    <button class="conflict-remove-btn" data-key="${item.key}" style="
                      background: #ffffff;
                      color: #d32f2f;
                      border: 2px solid #d32f2f;
                      padding: 12px 20px;
                      border-radius: 0;
                      cursor: pointer;
                      font-family: 'Poppins', Arial, sans-serif;
                      font-weight: 500;
                      font-size: 14px;
                      transition: all 0.3s ease;
                      flex: 1;
                      min-width: 120px;
                    " onmouseover="this.style.background='#d32f2f'; this.style.color='#ffffff'" onmouseout="this.style.background='#ffffff'; this.style.color='#d32f2f'">Remove Item</button>
                    
                    ${item.availableQty > 0 ? `<button class="conflict-proceed-btn" data-key="${item.key}" style="
                      background: #0868b1;
                      color: #ffffff;
                      border: 2px solid #0868b1;
                      padding: 12px 20px;
                      border-radius: 0;
                      cursor: pointer;
                      font-family: 'Poppins', Arial, sans-serif;
                      font-weight: 500;
                      font-size: 14px;
                      transition: all 0.3s ease;
                      flex: 1;
                      min-width: 120px;
                    " onmouseover="this.style.background='#ffffff'; this.style.color='#0868b1'" onmouseout="this.style.background='#0868b1'; this.style.color='#ffffff'">Use Available (${item.availableQty})</button>` : ''}
                  </div>
                </div>
              `).join('')}
            </div>
            
            <div style="text-align: center; padding-top: 20px; border-top: 2px solid #f3f3f3;">
              <button id="close-conflict-popup" style="
                background: #f3f3f3;
                color: #121212;
                border: 2px solid #f3f3f3;
                padding: 12px 30px;
                border-radius: 0;
                cursor: pointer;
                font-family: 'Poppins', Arial, sans-serif;
                font-weight: 500;
                font-size: 16px;
                transition: all 0.3s ease;
              " onmouseover="this.style.background='#121212'; this.style.color='#ffffff'; this.style.borderColor='#121212'" onmouseout="this.style.background='#f3f3f3'; this.style.color='#121212'; this.style.borderColor='#f3f3f3'">Close</button>
            </div>
          </div>
        </div>
      `;

      // Add popup to DOM
      document.body.insertAdjacentHTML('beforeend', popupHTML);
      
      const popup = document.getElementById('inventory-conflict-popup');
      
      // Handle remove buttons
      const removeButtons = popup.querySelectorAll('.conflict-remove-btn');
        removeButtons.forEach(btn => {
        btn.addEventListener('click', async (e) => {
          const key = e.target.dataset.key;
          
          // Disable the button to prevent double-clicks
          e.target.disabled = true;
          e.target.textContent = 'Removing...';
          
          try {
            // Wait for removal to complete
            await this.removeItemFromCart(key);
            
            // Force refresh cart data after removal - wait for completion
            this.cartData = null; // Clear cached data
            this.cartData = await this.fetchCartData();
            // Check if cart is now empty
            if (!this.cartData || this.cartData.length === 0) {
              window.location.reload();
              return;
            }
            
            // Remove this item from the popup
            e.target.closest('[data-key]').remove();
            this.conflictItems = this.conflictItems.filter(item => item.key !== key);
            
            // Check if there are any remaining conflict items in the popup
            if (this.conflictItems.length === 0) {
              // Close current popup
              popup.remove();
              
              // Re-validate the entire cart to check for any new conflicts
              const freshConflicts = await this.checkInventoryConflicts();
              
              if (freshConflicts.length > 0) {
                // New conflicts found, show popup again
                await this.showInventoryConflictPopup();
              } else {
                // No conflicts, proceed to checkout
                this.proceedToCheckout(this.currentCheckoutButton);
              }
              resolve();
            }
          } catch (error) {
            // Re-enable button on error
            e.target.disabled = false;
            e.target.textContent = 'Remove Item';
          }
        });
      });
      
      // Handle proceed buttons
      const proceedButtons = popup.querySelectorAll('.conflict-proceed-btn');
      proceedButtons.forEach(btn => {
        btn.addEventListener('click', async (e) => {
          const key = e.target.dataset.key;
          const item = this.conflictItems.find(item => item.key === key);
          
          if (!item) {
            return;
          }
          
          // Disable the button to prevent double-clicks
          e.target.disabled = true;
          e.target.textContent = 'Updating...';
          
          try {
            // Wait for cart update to complete
            if (item.availableQty > 0) {
              const newDesiredQty = (item.availableQty * 0.25).toString() + 'Yards';
              await this.updateCartItem(key, item.availableQty, newDesiredQty);
            } else {
              await this.removeItemFromCart(key);
            }
            
            // Force refresh cart data after update/removal - wait for completion
            this.cartData = null; // Clear cached data
            this.cartData = await this.fetchCartData();
            
            // Check if cart is now empty
            if (!this.cartData || this.cartData.length === 0) {
              window.location.reload();
              return;
            }
            
            // Remove this item from the popup
            e.target.closest('[data-key]').remove();
            this.conflictItems = this.conflictItems.filter(item => item.key !== key);
            
            // Check if there are any remaining conflict items in the popup
            if (this.conflictItems.length === 0) {
              // Close current popup
              popup.remove();
              
              // Re-validate the entire cart to check for any new conflicts
              const freshConflicts = await this.checkInventoryConflicts();
              
              if (freshConflicts.length > 0) {
                // New conflicts found, show popup again
                await this.showInventoryConflictPopup();
              } else {
                // No conflicts, proceed to checkout
                this.proceedToCheckout(this.currentCheckoutButton);
              }
              resolve();
            }
          } catch (error) {
            // Re-enable button on error
            e.target.disabled = false;
            e.target.textContent = `Use Available (${item.availableQty})`;
          }
        });
      });
      
        // Handle close button
        document.getElementById('close-conflict-popup').addEventListener('click', () => {
          popup.remove();
          // Reset checkout flag and re-enable buttons when popup is closed
          this.isCheckoutInProgress = false;
          this.enableAllCheckoutButtons();
          // Reset button text if available
          if (this.currentCheckoutButton && this.originalButtonText) {
            this.currentCheckoutButton.disabled = false;
            this.currentCheckoutButton.textContent = this.originalButtonText;
          }
          resolve();
        });
    });
  }

  /**
   * Validate and fix yards matching in background (no popup)
   * Returns: boolean indicating if any updates were made
   */
  async validateAndFixYardsMatching() {
    let hasUpdates = false;
    
    // Only process items that have Desired Quantity property
    const itemsToUpdate = this.cartData.filter(item => this.hasDesiredQuantityProperty(item));
    
    if (itemsToUpdate.length === 0) {
      return hasUpdates;
    }
    
    // Check each item for yards mismatch
    for (const item of itemsToUpdate) {
      const expectedDesiredQty = (item.quantity * 0.25).toString() + 'Yards';
      const currentDesiredQty = this.getDesiredQuantityProperty(item);
      
      if (currentDesiredQty !== expectedDesiredQty) {
        try {
          // Update the item in the background
          await this.updateCartItem(item.key, item.quantity, expectedDesiredQty);
          hasUpdates = true;
        } catch (error) {
          // Continue with other items even if one fails
        }
      }
    }
    
    return hasUpdates;
  }

  /**
   * Validate and fix Desired Quantity properties
   * Returns: boolean indicating if any updates were made
   */
  async validateDesiredQuantities(debugOnly = false) {
    let hasUpdates = false;
    
    for (const item of this.cartData) {
      const expectedDesiredQty = (item.quantity * 0.25).toString() + 'Yards';
      const currentDesiredQty = this.getDesiredQuantityProperty(item);
      
      if (currentDesiredQty !== expectedDesiredQty) {
        if (debugOnly) {
          hasUpdates = true;
        } else {
          await this.updateCartItem(item.key, item.quantity, expectedDesiredQty);
          hasUpdates = true;
        }
      }
    }
    
    // If we made updates and not in debug mode, refresh cart data to ensure consistency
    if (hasUpdates && !debugOnly) {
      this.cartData = await this.fetchCartData();
    }
    
    return hasUpdates;
  }

  /**
   * Get Desired Quantity property from item
   */
  getDesiredQuantityProperty(item) {
    if (!item.properties) {
      return '';
    }
    
    const desiredQtyProp = item.properties.find(prop => 
      Array.isArray(prop) && prop[0] === 'Desired Quantity'
    );
    
    const result = desiredQtyProp ? desiredQtyProp[1] : '';
    return result;
  }

  /**
   * Get item title for display
   */
  getItemTitle(item) {
    // Use product title with variant title if available
    let title = item.product_title || item.title || 'Unknown Item';
    if (item.variant_title && item.variant_title !== 'Default Title') {
      title += ` - ${item.variant_title}`;
    }
    return title;
  }

  /**
   * Check if item has Desired Quantity property
   */
  hasDesiredQuantityProperty(item) {
    if (!item.properties) return false;
    
    // Handle properties as array of arrays format: [["Desired Quantity", "0.75Yards"]]
    if (Array.isArray(item.properties)) {
      return item.properties.some(prop => Array.isArray(prop) && prop[0] === 'Desired Quantity');
    }
    
    // Handle properties as object format: {"Desired Quantity": "0.75Yards"}
    return item.properties['Desired Quantity'];
  }

  /**
   * Remove item from cart
   */
  async removeItemFromCart(key) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      });
      
      if (!response.ok) {
        const responseText = await response.text();
        throw new Error('Failed to remove item from cart');
      }
      
      // Return the response data for confirmation
      const responseData = await response.json();
      return responseData;
      
    } catch (error) {
      throw error; // Re-throw to handle in calling function
    }
  }

  /**
   * Initialize checkout button interceptors - simplified for Liquid-based dummy buttons
   */
  initCheckoutInterceptors() {
    // The dummy buttons are now handled directly in Liquid templates
    // This method is kept for compatibility but no longer needed for button replacement
    
    // NO PAGE LOAD VALIDATION - conflicts only appear on checkout button click
  }

  // replaceCheckoutButton method removed - now handled in Liquid templates

  // Page load validation removed - conflicts only show on checkout button click

  /**
   * Handle checkout button click
   */
  async handleCheckoutClick(event, originalButton = null) {
    event.preventDefault();
    event.stopPropagation();

    // Prevent multiple simultaneous checkout requests
    if (this.isCheckoutInProgress) {
      return;
    }

    const button = event.target;
    const originalText = button.textContent;
    
    // Store button info for popup handlers
    this.currentCheckoutButton = button;
    this.originalButtonText = originalText;
    
    // Set checkout in progress flag
    this.isCheckoutInProgress = true;
    
    // Show loading state
    button.disabled = true;
    button.textContent = 'Processing...';
    
    // Disable all other checkout buttons to prevent multiple clicks
    this.disableAllCheckoutButtons();

    // No timeout - conflicts must be resolved manually by user
    // Only reset on actual errors, not for conflicts

    try {
      // Run cart validation
      const result = await this.validateAndSyncCart();
      
      if (result === 'conflicts') {
        // Inventory conflicts found - popup is shown and will handle the flow
        // Don't reset buttons here - let the popup handlers manage this
        // STOP HERE - don't proceed to checkout, let popup handle it
        return;
      } else {
        // No conflicts - show final processing message and proceed to checkout
        button.textContent = 'Finalizing...';
        this.proceedToCheckout(originalButton || button);
        // Clear stored button info after proceeding
        this.currentCheckoutButton = null;
        this.originalButtonText = null;
      }
    } catch (error) {
      // On error, reset button state and allow user to try again
      this.isCheckoutInProgress = false;
      this.enableAllCheckoutButtons();
      button.disabled = false;
      button.textContent = originalText;
      this.currentCheckoutButton = null;
      this.originalButtonText = null;
    }
  }

  /**
   * Disable all checkout buttons to prevent multiple clicks
   */
  disableAllCheckoutButtons() {
    const checkoutButtons = document.querySelectorAll('.cart__checkout-button, button[name="checkout"], .cart-dummy-checkout-btn, .cart-original-checkout-btn');
    checkoutButtons.forEach(btn => {
      btn.disabled = true;
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.6';
    });
  }

  /**
   * Enable all checkout buttons
   */
  enableAllCheckoutButtons() {
    const checkoutButtons = document.querySelectorAll('.cart__checkout-button, button[name="checkout"], .cart-dummy-checkout-btn, .cart-original-checkout-btn');
    checkoutButtons.forEach(btn => {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.style.opacity = '';
    });
  }

  /**
   * Proceed to checkout
   */
  proceedToCheckout(button) {
    window.location.href = '/checkout';
    return;
  
    // Reset the checkout flag since we're proceeding
    this.isCheckoutInProgress = false;
    
    // If this is a hidden original button, click it directly
    if (button && button.style.display === 'none') {
      // Temporarily show the button, enable it, click it, then hide it again
      button.style.display = '';
      button.disabled = false;
      button.style.pointerEvents = '';
      button.style.opacity = '';
      button.click();
      button.style.display = 'none';
      return;
    }
    
    // Try to find the original button if we have a dummy button
    const originalButton = button && button.id.includes('-dummy') 
      ? document.getElementById(button.id.replace('-dummy', '-original'))
      : button;
    
    if (originalButton && originalButton.id.includes('-original')) {
      // Use the original button - make sure it's enabled
      originalButton.style.display = '';
      originalButton.disabled = false;
      originalButton.style.pointerEvents = '';
      originalButton.style.opacity = '';
      originalButton.click();
      originalButton.style.display = 'none';
      return;
    }
    
    // For visible buttons, submit the form
    const form = (button && button.closest('form')) || document.querySelector('form[action="/cart"]');
    
    if (form) {
      // Add checkout parameter to existing form
      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = '1';
      form.appendChild(checkoutInput);
      form.submit();
    } else {
      // Create a temporary form
      const checkoutForm = document.createElement('form');
      checkoutForm.method = 'POST';
      checkoutForm.action = '/cart';
      
      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = '1';
      
      checkoutForm.appendChild(checkoutInput);
      document.body.appendChild(checkoutForm);
      checkoutForm.submit();
    }
  }

  /**
   * Update cart item quantity and properties
   */
  async updateCartItem(key, quantity, desiredQuantity) {
    try {
      // First get current item properties
      const currentItem = this.cartData.find(item => item.key === key);
      const currentProperties = currentItem ? currentItem.properties : [];
      
      // Update properties object
      const updatedProperties = {};
      
      // Convert array format to object format
      if (Array.isArray(currentProperties)) {
        currentProperties.forEach(prop => {
          if (Array.isArray(prop) && prop.length >= 2) {
            updatedProperties[prop[0]] = prop[1];
          }
        });
      }
      
      // Update Desired Quantity
      updatedProperties['Desired Quantity'] = desiredQuantity;
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          properties: updatedProperties
        })
      });
      
      if (!response.ok) {
        const responseText = await response.text();
        throw new Error('Failed to update cart item');
      }
      
      // Return the response data for confirmation
      const responseData = await response.json();
      return responseData;
      
    } catch (error) {
      throw error; // Re-throw to handle in calling function
    }
  }
}

// Global function to validate cart
window.validateAndSyncCart = async function(event, originalButton = null) {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  
  // Use the global cartManager instance to maintain state
  if (!window.cartManager) {
    window.cartManager = new CartManager();
  }
  
  // Use the handleCheckoutClick method which has the multiple-click protection
  return window.cartManager.handleCheckoutClick(event, originalButton);
};

// Initialize cart manager when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  window.cartManager = new CartManager();
  window.cartManager.initCheckoutInterceptors();
  
  // Re-initialize when cart content changes (for dynamic content)
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        // Check if new checkout buttons were added
        const hasCheckoutButtons = Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && (
            node.querySelector && node.querySelector('button[name="checkout"], input[name="checkout"]') ||
            (node.name === 'checkout' && (node.tagName === 'BUTTON' || node.tagName === 'INPUT'))
          )
        );
        
        if (hasCheckoutButtons) {
          window.cartManager.initCheckoutInterceptors();
        }
      }
    });
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  // You can uncomment this to auto-validate on page load
  // window.validateAndSyncCart();
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartManager;
}



